import React, { useState, useMemo } from "react";
//import NotoSans from "../pages/sales/NotoSans-Regular-normal.js";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    TextField,
    Autocomplete,
    Typography,
    TablePagination,
    Paper,
    Chip,
    Button
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { format } from 'date-fns';

export default function DataTable({
    data,
    columns,
    totals = {},
    formatDateSafe,
    billType = 'S',
    orderGroup = 'none',
    currency = 'all',
    filterDate = null,
    tradeLabel = "All"
}) {

    const exportColumnKeys = [
        "InvNo",
        "Bill_Date",
        "PartyName",
        "L_Code",
        "PktNo",
        "SL_Code",
        "SPktNo",
        "Weight",
        "Pcs",
        "Rate",
        "S_Rate",
        "Amount",
        "AmountD",
        "DiscPer",
        "DiscAmt",
        "NetAmtRs",
        "NETAMTD",
        "GSTAmt",
        "TDSAmt"
    ];

    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState({ key: null, dir: "asc" });
    const compName = useSelector((state) => state.company.compName);
    const selectedCompanyName = compName.length > 0 ? compName[0] : "N/A";
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const getUniqueValues = (key) =>
        [...new Set(data.map((item) => item[key]))].filter((v) => v !== undefined && v !== null);

    const handleSort = (key) => {
        setSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
        }));
    };

    const getSortIcon = (key) => {
        if (sort.key !== key) return faSort;
        return sort.dir === "asc" ? faSortUp : faSortDown;
    };


  const sortedData = useMemo(() => {
  if (!sort.key) return data;

  return [...data].sort((a, b) => {
    let A = a[sort.key];
    let B = b[sort.key];

    // Handle null/undefined
    if (A === null || A === undefined) A = "";
    if (B === null || B === undefined) B = "";

    // If both are numbers (or numeric strings), compare numerically
    const numA = parseFloat(A);
    const numB = parseFloat(B);
    const isNumeric = !isNaN(numA) && !isNaN(numB);

    if (isNumeric) {
      return sort.dir === "asc" ? numA - numB : numB - numA;
    }

    // Otherwise, compare as strings
    A = String(A).toLowerCase();
    B = String(B).toLowerCase();

    if (A < B) return sort.dir === "asc" ? -1 : 1;
    if (A > B) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });
}, [data, sort]);


    const filteredData = useMemo(() => {
        return sortedData.filter((row) =>
            Object.entries(filters).every(([key, selected]) => {
                if (!selected?.length) return true;
                return selected.includes(row[key]);
            })
        );
    }, [sortedData, filters]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, page, rowsPerPage]);
    const FilterName = {
        InvNo: 'Inv No.',
        Bill_Date: 'Date',
        YearMonth: 'Month',
        PartyName: 'Party',
        L_Code: 'Lot No.',
        PktNo: 'PktNo',
        Weight: 'Wt',
        Pcs: 'Pcs',
        Rate: 'Rate',
        Amount: 'Amt',
        DiscPer: 'Disc',
        DiscAmt: 'Disc Amt',
        NetAmtRs: 'Amt(₹)',
        GSTAmt: 'Gst',
        BrokerDesc: 'Broker',
        S_Name: 'Shape',
        Sh_Name: 'Color',
        Q_Name: 'Clarity',
        Stn_Lnth: 'Length',
        Dia: 'Dia',
        LabId: 'Lab',
        ReportNo: 'Report',
        Cut: 'Cut',
        polish: 'Polish',
        Symtry: 'Symtry',
        Flrn: 'Flrn'
    }
    const getColName = (colName) => {
        return FilterName[colName]
    }

    const handleRemoveTag = (field, value) => {
        const updated = filters[field].filter((v) => v !== value);
        setFilters(prev => ({ ...prev, [field]: updated }));
        //setFilters(prev => ({ ...prev, [field]: value }));
        // handleSelectChange(field, updated);
        // ⬆️ same function Autocomplete uses to update states
    };
    const exportToExcel = () => {
        const exportData = filteredData.map(row => {
            const obj = {};
            columns.forEach(col => {
                obj[col.label] =
                    col.key === "Bill_Date"
                        ? formatDateSafe(row[col.key], "dd/MM/yyyy")
                        : row[col.key];
            });
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        XLSX.writeFile(workbook, "report.xlsx");
    };
    const safeFormatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        if (isNaN(d)) return "";
        return d.toLocaleDateString("en-GB"); // dd/MM/yyyy
    };
    const getReportDateRange = () => {
        if (!filteredData.length) return { from: "", to: "" };

        const dates = filteredData
            .map(r => new Date(r.Bill_Date))
            .filter(d => !isNaN(d));

        if (!dates.length) return { from: "", to: "" };

        return {
            from: safeFormatDate(new Date(Math.min(...dates))),
            to: safeFormatDate(new Date(Math.max(...dates)))
        };
    };

    const exportToPDF = () => {
        const doc = new jsPDF({
            orientation: "landscape",
            unit: "mm"
        });

        doc.setFont("helvetica");
        doc.setFontSize(10);

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const contentWidth = pageWidth - 2 * margin;

        // ===== HEADER =====
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const companyName = compName.length ? compName.join(", ") : "Company";
        const companyNameWidth =
            (doc.getStringUnitWidth(companyName) * doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
        doc.text(
            companyName,
            margin + (contentWidth - companyNameWidth) / 2,
            20
        );

        doc.setFontSize(12);
        const reportTitle = `${billType === "S"
            ? "Sales"
            : filters.billType === "P"
                ? "Purchase"
                : "Return"
            } Report`;
        const reportTitleWidth =
            (doc.getStringUnitWidth(reportTitle) * doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
        doc.text(
            reportTitle,
            margin + (contentWidth - reportTitleWidth) / 2,
            27
        );

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        // const { from, to } = getReportDateRange();

        doc.text(`From Date: ${safeFormatDate(filterDate?.fromDate)}`, margin, 40);
        doc.text(`To Date: ${safeFormatDate(filterDate?.toDate)}`, margin + 60, 40);

        doc.line(margin, 44, pageWidth - margin, 44);

        // ===== HELPERS =====
        const toNumber = (val) => {
            if (val === null || val === undefined || val === "") return 0;
            const n = parseFloat(val);
            return isNaN(n) ? 0 : n;
        };

        // ===== COLUMN FILTER =====
        const hideIfZeroKeys = ["TDSAmt", "DiscAmt", "DiscPer","GSTAmt"];

        const shouldHideColumn = (key) =>
            hideIfZeroKeys.includes(key) &&
            filteredData.reduce((s, r) => s + toNumber(r[key]), 0) === 0;

        const exportColumn =
            orderGroup === "none"
                ? columns.filter(
                    (col) =>
                        exportColumnKeys.includes(col.key) &&
                        !shouldHideColumn(col.key)
                )
                : columns;


        // ===== TABLE DATA =====
        const tableColumn = exportColumn.map((col) =>
            col.label.replace("₹", "INR").replace("$", "$")
        );
        const formatNumber = (val) => {
            const n = Number(val);
            if (isNaN(n)) return "0.00"; // fallback
            return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };


        const tableRows = filteredData.map((row) =>
            exportColumn.map((col) => {
                const value = row[col.key];
                if (col.key === 'Bill_Date') {
                    return formatDateSafe(row[col.key], "dd/MM/yyyy")
                }
                //if (typeof value === 'number') return value.toFixed(2);
                if (!["PktNo","SL_Code","SPktNo","L_Code"].includes(col.key)&& (typeof value === 'number' || !isNaN(Number(value)))) return formatNumber(value);
                return value ?? '';
                // col.key === "Bill_Date"
                //     ? formatDateSafe(row[col.key], "dd/MM/yyyy")
                //     : row[col.key] ?? ""
            }

            )
        );

        // ===== TOTAL ROW =====
        const totalRow = exportColumn.map((col, idx) => {
            if (idx === 0) return "TOTAL";

            if (col.key === "PktNo") {
                return filteredData.length;
            }

            if (["Rate", "S_Rate"].includes(col.key)) {
                const weightedSum = filteredData.reduce(
                    (s, r) => s + toNumber((r[col.key])*(toNumber(r.Weight))),
                    0
                );
                const weightSum = filteredData.reduce(
                    (s, r) => s + toNumber(r.Weight),
                    0
                );
                return weightSum ? (weightedSum / weightSum).toFixed(2) : "";
            }

            const isNumeric = filteredData.some(
                (r) => !isNaN(Number(r[col.key]))
            );

            if (isNumeric && ["Amount", "AmountD", "TDSAmt", "Pcs", "NetAmtRs", "NETAMTD", "GSTAmt", "Weight", "DiscAmt"].includes(col.key)) {
                return filteredData
                    .reduce((s, r) => s + toNumber(r[col.key]), 0)
                    .toFixed(2);
            }

            return "";
        });

        tableRows.push(totalRow);

        // ===== PDF TABLE =====
        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 47,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                halign: "right",
                valign: "middle"
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: "bold",
                halign: "center"
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            theme: "grid",

            didParseCell: function (dataCell) {
                // First column left aligned
                if (dataCell.column.index === 0) {
                    dataCell.cell.styles.halign = 'left';
                }
                 if ( orderGroup=='none'&& [1,3,4,5].includes(dataCell.column.index)) {
                    dataCell.cell.styles.halign = 'center';
                }

                // Bold and centered for totals row
                if (dataCell.row.index === filteredData.length) {
                    dataCell.cell.styles.fontStyle = 'bold';
                    dataCell.cell.styles.fillColor = [220, 220, 220];
                    dataCell.cell.styles.textColor = [0, 0, 0];
                    if (dataCell.column.index === 0) {
                        dataCell.cell.styles.halign = 'center';
                    }
                }

                // Prevent word breaking in headers
                if (dataCell.row.index === 0) {
                    dataCell.cell.styles.cellPadding = { top: 2, right: 2, bottom: 2, left: 2 };
                }

                // Better handling of long text
                if (typeof dataCell.cell.raw === 'string' && dataCell.cell.raw.length > 15) {
                    dataCell.cell.styles.fontSize = 7;
                }
            },
            willDrawCell: function (dataCell) {
                // Add borders to all cells
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.1);
                doc.line(
                    dataCell.cell.x,
                    dataCell.cell.y + dataCell.cell.height,
                    dataCell.cell.x + dataCell.cell.width,
                    dataCell.cell.y + dataCell.cell.height
                );
            },
            willDrawPage: function (data) {
                // Add page numbers
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
                }
            }
        });

        const filename = `${selectedCompanyName}_${billType === 'S' ? 'Sales' : billType === 'P' ? 'Purchase' : 'Return'}_${currency}_Report_${format(new Date(), 'dd/MM/yyyy')}.pdf`;
        doc.save(filename);
    };


    return (
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            {/* Pagination */}
            {data.length > 0 ?
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        sx={{
                            backgroundColor:"#bd0606ff",
                            color:"#FFFFFF",
                            border:"1px solid #bd0606ff"
                        }}
                        onClick={exportToExcel}
                    >
                        Export Excel
                    </Button>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={exportToPDF}
                        sx={{
                            backgroundColor:"#045f08ff",
                            color:"#FFFFFF",
                            border:"1px solid #045f08ff"
                        }}
                    >
                        Export PDF
                    </Button>
                </Box> : ""}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 0,
                gap: 1,
                p: 1
            }}>
                <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: "1rem",
                    margin: "0px",
                    padding: "0px"
                }}>
                    {data.length > 0 ?
                        `Showing ${filteredData.length} of ${data.length} records` :
                        'No data available'}
                </Typography>

                {data.length > 0 && (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                    }}>
                        <Chip
                            label={`Trade: ${tradeLabel}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            label={`From: ${format(filterDate.fromDate, 'dd-MMM-yyyy')}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                        <Chip
                            label={`To: ${format(filterDate.toDate, 'dd-MMM-yyyy')}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                    </Box>
                )}
            </Box>
            <div style={{ display: "flex", flexDirection: "row", gap: 4, }}>
                {Object.entries(filters).map(([field, values]) => (
                    <div key={field} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <strong style={{ fontSize: "0.6rem" }} className="text-xs uppercase"> {values.length > 0 ? getColName(field) + " :" : ""} </strong>
                        {values.map(value => (
                            <Chip
                                key={`${field}-${value}`}
                                label={value}
                                onDelete={() => handleRemoveTag(field, value)}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "11px" }}
                            />
                        ))}
                    </div>
                ))}
            </div>

            {/* Scrollable Table */}
            <Box sx={{ width: "100%", overflowX: "auto", maxHeight: 500, overflowY: "auto" }}>
                <Table sx={{ minWidth: "max-content", borderCollapse: "separate", borderSpacing: 0, border: "1px solid #dbdbdbff" }}>
                    {/* Header */}
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    sx={{
                                        background: "#eeeeeeff",
                                        color: "#131212ff",
                                        fontWeight: "bold",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 5,
                                        minWidth: 100,
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Box display="flex" alignItems="center" justifyContent="space-evenly" sx={{ cursor: "pointer", textTransform: "uppercase" }} onClick={() => handleSort(col.key)}>
                                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, textAlign: "center" }} >{col.label}</Typography>
                                        <FontAwesomeIcon icon={getSortIcon(col.key)} style={{ display: "flex", justifyContent: "flex-end", fontSize: 12, color: "#4a60dbff" }} />
                                    </Box>

                                    {/* Inline Filter */}
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={getUniqueValues(col.key)}
                                        value={filters[col.key] || []}
                                        onChange={(e, value) => setFilters((prev) => ({ ...prev, [col.key]: value }))}
                                        //renderInput={(params) => <TextField {...params} placeholder="Filter…" size="small" />}
                                        // sx={{ mt: 1 }}
                                        noOptionsText="No options available"
                                        renderTags={() => null}
                                        slotProps={{
                                            chip: {
                                                size: "small",
                                                sx: { height: 20, fontSize: "10px", padding: "0 4px", "& .MuiChip-label": { padding: 0 } }
                                            },
                                            input: { sx: { padding: "2px 0px !important", fontSize: "11px" } },
                                            popper: { sx: { fontSize: "12px" } },
                                            listbox: {
                                                sx: {
                                                    fontSize: "11px",
                                                    "& .MuiAutocomplete-option": { padding: "2px 8px", minHeight: "28px" }
                                                }
                                            }
                                        }}
                                        sx={{
                                            "& .MuiAutocomplete-inputRoot": { padding: "0px 4px !important", minHeight: "30px", background: "#FFFFFF" },
                                            "& .MuiOutlinedInput-root": { padding: "0px !important", height: "30px" }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Find..."
                                                variant="outlined"
                                                size="small"
                                                inputProps={{
                                                    ...params.inputProps, // ⚡ make sure inputProps exist
                                                    autoComplete: "off"
                                                }}
                                                sx={{
                                                    width: ["S_Name", "BrokerDesc", "Party"].includes(col.key) ? "150px" : "100px",
                                                    "& .MuiInputLabel-root": { fontSize: "11px" }
                                                }}
                                            />
                                        )}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* Body */}
                    <TableBody>
                        {paginatedData.length ? (
                            paginatedData.map((row, idx) => (
                                <TableRow
                                    key={idx}
                                    hover
                                    sx={{

                                        "&:nth-of-type(odd)": { background: "#f9f9f9" },
                                        "&:hover": { background: "#e3f2fd" },
                                    }}
                                >
                                    {columns.map((col) => (
                                        <TableCell key={col.key} sx={{ px: 1.5, py: 0.8, fontSize: "0.8rem", }}>
                                            {col.key === "Bill_Date"
                                                ? formatDateSafe(row[col.key], "dd/MM/yyyy")
                                                : typeof row[col.key] === "number"
                                                    ? row[col.key].toFixed(2)
                                                    : row[col.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                                    No matching records found
                                </TableCell>
                            </TableRow>
                        )}

                        {/* Sticky Footer */}
                        {/* GRAND TOTAL */}
                        <TableRow sx={{ position: "sticky", bottom: 0, background: "#f1f1f1ff", zIndex: 10 }}>
                            {columns.map((col, idx) => {
                                // Calculate total only for numeric columns
                                let colTotal = 0

                                if (col.key == 'Rate' || col.key == 'S_Rate') {
                                    const weightSum = filteredData.reduce(
                                        (sum, row) => sum + (row.Weight || 0),
                                        0
                                    );

                                    if (col.key == 'S_Rate') {
                                        const rateDSum = filteredData.reduce(
                                            (sum, row) => sum + ((row.S_Rate || 0)*(row.Weight || 0)),
                                            0
                                        );
                                        colTotal = weightSum > 0 ? rateDSum / weightSum : 0
                                    }
                                    else {
                                        const rateSum = filteredData.reduce(
                                            (sum, row) => sum + ((row.Rate || 0)*(row.Weight || 0)),
                                            0
                                        );
                                        colTotal = weightSum > 0 ? rateSum / weightSum : 0
                                    }
                                }
                                else if (col.key == 'PktNo') {
                                    colTotal = filteredData.reduce(
                                        (count, row) => count + 1,
                                        0
                                    )
                                }
                                else
                                    colTotal =
                                        filteredData.length && typeof filteredData[0][col.key] === "number"
                                            ? filteredData.reduce((sum, row) => sum + (row[col.key] || 0), 0)
                                            : "";

                                return (
                                    <TableCell key={col.key} sx={{ fontWeight: "bold", px: 1.5, py: 0.8 }}>
                                        {idx === 0
                                            ? "Total:"
                                            : colTotal !== ""
                                                ? colTotal.toFixed(2)
                                                : ""}
                                    </TableCell>
                                );
                            })}
                        </TableRow>


                    </TableBody>
                </Table>
            </Box>
            <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100, 250]}
                sx={{ mb: 2 }}
            />
        </Paper>
    );
}
